/*
  Carter & Me — global chrome behaviour.

  Two custom elements: the "More" dropdown and the mobile nav toggle.

  The handoff calls out two bugs in the prototype worth not repeating:
  close the menu on `mouseleave` of the wrapper, never `mouseout` (which
  bubbles from children and shuts the menu the instant the pointer lands on
  an item), and toggle with `display`, not `opacity` (an opacity-hidden
  panel still swallows clicks and stays in the tab order).
*/

class CmMenu extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-cm-menu-trigger]');
    this.panel = this.querySelector('[data-cm-menu-panel]');
    if (!this.trigger || !this.panel) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);

    // Hover intent lives on the wrapper so moving onto an item keeps it open.
    this.addEventListener('mouseenter', this.open);
    this.addEventListener('mouseleave', this.close);

    this.trigger.addEventListener('click', this.onToggle);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusout', this.onFocusOut);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.open);
    this.removeEventListener('mouseleave', this.close);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  onToggle(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.trigger.focus();
      return;
    }

    // Down arrow on the trigger opens and lands on the first item.
    if (event.key === 'ArrowDown' && event.target === this.trigger) {
      event.preventDefault();
      this.open();
      const first = this.panel.querySelector('a');
      if (first) first.focus();
    }
  }

  onFocusOut(event) {
    // Tabbing out of the wrapper entirely closes it; moving between items does not.
    if (!this.contains(event.relatedTarget)) this.close();
  }
}

customElements.define('cm-menu', CmMenu);

class CmMobileNav extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-cm-burger]');
    this.panel = this.querySelector('[data-cm-mobile-panel]');
    if (!this.trigger || !this.panel) return;

    this.close = this.close.bind(this);

    this.trigger.addEventListener('click', () => {
      this.panel.classList.contains('is-open') ? this.close() : this.open();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.panel.classList.contains('is-open')) {
        this.close();
        this.trigger.focus();
      }
    });

    // A link tap should not leave the panel hanging open behind the new page.
    this.panel.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.close();
    });
  }

  open() {
    this.panel.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('cm-mobile-nav', CmMobileNav);

/*
  Floating bag.

  Registered as `cart-notification` so Dawn's product-form finds it and
  drives it: it asks getSectionsToRender() which sections to request, posts
  the add, then calls renderContents() with the rendered markup. Dawn's own
  cart-notification.js is not loaded, so the name is free — but guard the
  define in case an app or a future Dawn update brings it back.
*/
if (!customElements.get('cart-notification')) {
  class CmFloatingBag extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cm-bag-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    getSectionsToRender() {
      return [{ id: 'cm-floating-bag', section: 'cm-floating-bag', selector: '.cm-bag__inner' }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(parsedState) {
      const section = this.getSectionsToRender()[0];
      const html = parsedState.sections && parsedState.sections[section.id];

      if (html) {
        const fresh = new DOMParser().parseFromString(html, 'text/html');
        const inner = fresh.querySelector(section.selector);
        const source = fresh.querySelector('cart-notification');
        if (inner) this.querySelector(section.selector).innerHTML = inner.innerHTML;
        if (source) this.dataset.cartCount = source.dataset.cartCount;
      }

      this.updateHeaderCount();
      this.open();
    }

    // The header shows Bag (n) as plain text rather than Dawn's bubble section.
    updateHeaderCount() {
      const count = this.dataset.cartCount;
      if (count == null) return;
      document.querySelectorAll('[data-cm-bag-count]').forEach((el) => {
        el.textContent = count;
      });
    }

    open() {
      this.classList.add('is-open');
    }

    close() {
      this.classList.remove('is-open');
      if (this.activeElement && document.contains(this.activeElement)) this.activeElement.focus();
    }
  }

  customElements.define('cart-notification', CmFloatingBag);
}
